import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import MessageBubble from './MessageBubble';
import { uploadMedia } from '../services/api';
import API from '../services/api';

export default function ChatWindow({ onOpenSidebar }) {
  const { activeUser, messages, sendMessage, typingUsers, onlineUsers, loadMoreMessages, hasMore } = useChat();
  const { user: admin } = useAuth();
  const [input, setInput]               = useState('');
  const [uploading, setUploading]       = useState(false);
  const [replyTo, setReplyTo]           = useState(null);
  const [isMobile, setIsMobile]         = useState(window.innerWidth <= 768);

  // Saved replies state
  const [showReplies, setShowReplies]   = useState(false);
  const [allReplies, setAllReplies]     = useState([]);
  const [suggestions, setSuggestions]   = useState([]);
  const [suggestionIdx, setSuggestionIdx] = useState(0);

  const bottomRef = useRef(null);
  const fileRef   = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load all saved replies once
  useEffect(() => {
    API.get('/saved-replies')
      .then(({ data }) => setAllReplies(data))
      .catch(() => {});
  }, []);

  // Watch input for /shortcut pattern
  useEffect(() => {
    if (input.startsWith('/') && input.length > 1) {
      const query = input.slice(1).toLowerCase();
      const matches = allReplies.filter(r =>
        r.shortcut.includes(query) || r.title.toLowerCase().includes(query)
      );
      setSuggestions(matches);
      setSuggestionIdx(0);
    } else {
      setSuggestions([]);
    }
  }, [input, allReplies]);

  const applyReply = (reply) => {
    setInput(reply.content);
    setSuggestions([]);
    setShowReplies(false);
    inputRef.current?.focus();
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || !activeUser) return;
    sendMessage({ type: 'text', content: text, replyTo: replyTo?._id });
    setInput('');
    setReplyTo(null);
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const handleKey = (e) => {
    // Navigate suggestions with arrow keys
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSuggestionIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSuggestionIdx(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); applyReply(suggestions[suggestionIdx]); return; }
      if (e.key === 'Escape') { setSuggestions([]); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeUser) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('receiverId', activeUser._id);
      if (replyTo) fd.append('replyTo', replyTo._id);
      await uploadMedia(fd);
      setReplyTo(null);
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const getInitials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  if (!activeUser) return (
    <div style={s.empty}>
      {isMobile && <button style={s.openSidebarBtn} onClick={onOpenSidebar}>← View Conversations</button>}
      <div style={{ fontSize: '52px', marginBottom: '12px' }}>💬</div>
      <div style={s.emptyTitle}>Select a conversation</div>
      <div style={s.emptySub}>Choose a player from the list to start chatting</div>
    </div>
  );

  const isOnline = onlineUsers[activeUser._id];
  const isTyping = typingUsers[activeUser._id];

  return (
    <div style={s.wrap}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          {isMobile && <button style={s.backBtn} onClick={onOpenSidebar}>←</button>}
          <div style={s.headerAvatarWrap}>
            <div style={s.headerAvatar}>{getInitials(activeUser.name)}</div>
            {isOnline && <div style={s.headerOnline} />}
          </div>
          <div>
            <div style={s.headerName}>{activeUser.name}</div>
            <div style={s.headerStatus}>
              {isTyping ? <span style={{ color: '#00ffb4' }}>✏️ typing...</span>
                : isOnline ? <span style={{ color: '#00ffb4' }}>🟢 Online</span>
                : <span style={{ color: 'rgba(255,255,255,.3)' }}>⚫ Offline</span>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          {activeUser.isVip     && <span style={s.vipTag}>⭐ VIP</span>}
          {activeUser.isBlocked && <span style={s.blockedTag}>🚫 Blocked</span>}
          {activeUser.labels?.map(label => (
            <span key={label} style={s.labelTag}>{label}</span>
          ))}
        </div>
      </div>

      {hasMore && (
        <div style={{ padding: '8px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <button style={s.loadMoreBtn} onClick={loadMoreMessages}>↑ Load older messages</button>
        </div>
      )}

      {/* Messages */}
      <div style={s.messages}>
        {messages.length === 0 && <div style={s.noMsgs}>No messages yet — say hello! 👋</div>}
        {messages.map(msg => (
          <MessageBubble key={msg._id} message={msg}
            isOwn={msg.senderType === 'admin'} onReply={setReplyTo} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Reply bar */}
      {replyTo && (
        <div style={s.replyBar}>
          <div>
            <div style={s.replyLabel}>↩ Replying to</div>
            <div style={s.replyText}>{replyTo.content?.slice(0, 50)}...</div>
          </div>
          <button style={s.replyClose} onClick={() => setReplyTo(null)}>✕</button>
        </div>
      )}

      {/* Slash shortcut suggestions dropdown */}
      {suggestions.length > 0 && (
        <div style={s.suggestionsBox}>
          <div style={s.suggestionsHeader}>📋 Saved Replies — Tab/Enter to select</div>
          {suggestions.map((r, i) => (
            <div key={r._id}
              style={{ ...s.suggestionItem, ...(i === suggestionIdx ? s.suggestionItemActive : {}) }}
              onClick={() => applyReply(r)}
              onMouseEnter={() => setSuggestionIdx(i)}>
              <span style={s.suggestionShortcut}>/{r.shortcut}</span>
              <span style={s.suggestionTitle}>{r.title}</span>
              <span style={s.suggestionPreview}>{r.content.slice(0, 60)}...</span>
            </div>
          ))}
        </div>
      )}

      {/* Saved Replies Panel */}
      {showReplies && (
        <div style={s.repliesPanel}>
          <div style={s.repliesPanelHeader}>
            <span style={{ color: '#00ffb4', fontWeight: '700', fontSize: '13px' }}>📋 Saved Replies</span>
            <button style={s.replyClose} onClick={() => setShowReplies(false)}>✕</button>
          </div>
          <div style={s.repliesList}>
            {allReplies.length === 0 && (
              <div style={{ color: 'rgba(255,255,255,.3)', fontSize: '13px', padding: '20px', textAlign: 'center' }}>
                No saved replies yet. Type /shortcut to use one.
              </div>
            )}
            {allReplies.map(r => (
              <div key={r._id} style={s.replyItem} onClick={() => applyReply(r)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                  <span style={s.suggestionShortcut}>/{r.shortcut}</span>
                  <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '600' }}>{r.title}</span>
                </div>
                <div style={{ color: 'rgba(255,255,255,.35)', fontSize: '12px', lineHeight: '1.4' }}>
                  {r.content.slice(0, 80)}{r.content.length > 80 ? '...' : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div style={s.inputArea}>
        <button style={s.attachBtn} onClick={() => fileRef.current?.click()} disabled={uploading} title="Attach file">
          {uploading ? '⏳' : '📎'}
        </button>
        <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={handleFile}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx" />

        <button
          style={{ ...s.attachBtn, color: showReplies ? '#00ffb4' : 'rgba(255,255,255,.4)', borderColor: showReplies ? 'rgba(0,255,180,.3)' : 'rgba(255,255,255,.08)' }}
          onClick={() => setShowReplies(!showReplies)}
          title="Saved Replies">
          📋
        </button>

        <div style={{ flex: 1, position: 'relative' }}>
          <textarea
            ref={inputRef}
            style={s.input}
            placeholder="Type a message or /shortcut for saved replies..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            rows={1}
          />
        </div>

        <button style={{ ...s.sendBtn, opacity: input.trim() ? 1 : 0.4 }}
          onClick={handleSend} disabled={!input.trim()}>➤</button>
      </div>
    </div>
  );
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', height: '100%', background: '#080c14', overflow: 'hidden' },
  empty: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#080c14', padding: '20px' },
  openSidebarBtn: { background: 'rgba(0,255,180,.08)', border: '1px solid rgba(0,255,180,.2)', borderRadius: '10px', color: '#00ffb4', padding: '10px 20px', cursor: 'pointer', fontSize: '13px', fontFamily: "'Exo 2',sans-serif", marginBottom: '24px' },
  emptyTitle: { color: 'rgba(255,255,255,.5)', fontSize: '18px', fontWeight: '600', fontFamily: "'Rajdhani',sans-serif" },
  emptySub: { color: 'rgba(255,255,255,.25)', fontSize: '13px', marginTop: '8px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#0d1420', borderBottom: '1px solid rgba(0,255,180,.08)', flexShrink: 0, gap: '10px' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 },
  backBtn: { background: 'rgba(0,255,180,.08)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '8px', color: '#00ffb4', fontSize: '16px', cursor: 'pointer', padding: '5px 10px', flexShrink: 0 },
  headerAvatarWrap: { position: 'relative', flexShrink: 0 },
  headerAvatar: { width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(135deg,#00ffb4,#00d4ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#080c14', fontSize: '13px', fontWeight: '900', fontFamily: "'Rajdhani',sans-serif" },
  headerOnline: { position: 'absolute', bottom: '-1px', right: '-1px', width: '10px', height: '10px', borderRadius: '50%', background: '#00ffb4', border: '2px solid #0d1420' },
  headerName: { color: '#e2e8f0', fontSize: '14px', fontWeight: '700', fontFamily: "'Rajdhani',sans-serif" },
  headerStatus: { fontSize: '11px', marginTop: '1px' },
  vipTag: { background: 'rgba(255,215,0,.12)', border: '1px solid rgba(255,215,0,.3)', color: '#ffd700', borderRadius: '7px', padding: '3px 8px', fontSize: '10px', fontWeight: '700' },
  blockedTag: { background: 'rgba(255,50,50,.1)', border: '1px solid rgba(255,50,50,.2)', color: '#ff6b6b', borderRadius: '7px', padding: '3px 8px', fontSize: '10px', fontWeight: '700' },
  labelTag: { background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.2)', color: '#00d4ff', borderRadius: '7px', padding: '3px 8px', fontSize: '10px', fontWeight: '600' },
  loadMoreBtn: { background: 'rgba(0,255,180,.06)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '9px', color: 'rgba(0,255,180,.7)', padding: '6px 16px', cursor: 'pointer', fontSize: '12px', fontFamily: "'Exo 2',sans-serif" },
  messages: { flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: '6px' },
  noMsgs: { textAlign: 'center', color: 'rgba(255,255,255,.2)', fontSize: '13px', padding: '40px 0' },
  replyBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,255,180,.05)', borderTop: '1px solid rgba(0,255,180,.1)', padding: '8px 14px', flexShrink: 0 },
  replyLabel: { color: 'rgba(0,255,180,.7)', fontSize: '10px', fontWeight: '700', letterSpacing: '1px' },
  replyText: { color: 'rgba(255,255,255,.4)', fontSize: '12px' },
  replyClose: { background: 'transparent', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', fontSize: '14px', padding: '4px 8px' },

  suggestionsBox: { position: 'relative', background: '#0d1420', borderTop: '1px solid rgba(0,255,180,.15)', maxHeight: '220px', overflowY: 'auto', flexShrink: 0 },
  suggestionsHeader: { padding: '8px 14px', fontSize: '10px', color: 'rgba(0,255,180,.5)', fontWeight: '700', letterSpacing: '1px', borderBottom: '1px solid rgba(0,255,180,.06)' },
  suggestionItem: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '10px 14px', cursor: 'pointer', transition: 'background .15s' },
  suggestionItemActive: { background: 'rgba(0,255,180,.08)' },
  suggestionShortcut: { color: '#00ffb4', fontSize: '11px', fontWeight: '700', letterSpacing: '1px', background: 'rgba(0,255,180,.08)', borderRadius: '5px', padding: '1px 6px', display: 'inline-block' },
  suggestionTitle: { color: '#e2e8f0', fontSize: '13px', fontWeight: '600', marginLeft: '6px' },
  suggestionPreview: { color: 'rgba(255,255,255,.3)', fontSize: '11px', lineHeight: '1.4' },

  repliesPanel: { background: '#0d1420', borderTop: '1px solid rgba(0,255,180,.15)', maxHeight: '280px', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  repliesPanelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid rgba(0,255,180,.08)' },
  repliesList: { overflowY: 'auto', flex: 1 },
  replyItem: { padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,.04)', transition: 'background .15s' },

  inputArea: { display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '10px 12px', background: '#0d1420', borderTop: '1px solid rgba(0,255,180,.08)', flexShrink: 0 },
  attachBtn: { background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: '10px', color: 'rgba(255,255,255,.4)', fontSize: '18px', cursor: 'pointer', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .2s' },
  input: { width: '100%', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '12px', padding: '10px 14px', color: '#fff', fontSize: '14px', fontFamily: "'Exo 2',sans-serif", outline: 'none', resize: 'none', minHeight: '40px', maxHeight: '120px', overflowY: 'auto', lineHeight: '1.4', boxSizing: 'border-box' },
  sendBtn: { background: 'linear-gradient(135deg,#00ffb4,#00d4ff)', border: 'none', borderRadius: '10px', color: '#080c14', fontSize: '18px', cursor: 'pointer', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'opacity .2s' },
};
